import { Media, ProductType } from '#types/generated/graphql-types';
import Link from 'next/link';
import Button from '#components/controls/button/Button';
import dayjs from 'dayjs';
import Typography from '#components/typography/Typography';
import IconButton from '#components/controls/icon-button/IconButton';
import { PlayArrow } from '#components/icons';
import { ContinueWatchingItem } from '#store/namespaces/continue-watching/state';
import ProgressBar from '#components/feedback/progress-bar/ProgressBar';
import { getTranslatedMediaDuration } from '#utils/getMediaDuration';
import useTranslation from 'next-translate/useTranslation';
import { getRoute } from '#utils';
import shouldShowPlayButton from '#utils/shouldShowPlayButton';
import shouldShowRentButton from '#utils/shouldShowRentButton';

type Props = {
  item: Media;
  continueWatchingState: ContinueWatchingItem;
  onPlayButtonClick: (item: Media) => void;
  onProductButtonClick: (mediaIri, productIri) => void;
};

const EpisodeItemLarge = ({
  item,
  continueWatchingState,
  onPlayButtonClick,
  onProductButtonClick,
}: Props) => {
  const { t, lang } = useTranslation();

  const duration = continueWatchingState?.mediaDuration;
  const time = continueWatchingState?.currentTime;

  const rentProduct = item?.products?.availableProducts?.find(
    (product) => product.type === ProductType.Rent
  );

  return (
    <div className="tw-flex tw-mb-4" style={{ backgroundColor: '#11151f' }}>
      <div className="tw-shrink-0 tw-relative">
        <img
          alt={item.title}
          src={item.teaserImageFormats.sixteenByNine.medium}
          width="213"
          height="120"
        />
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-flex-col">
          {shouldShowPlayButton(item?.products) === true && (
            <>
              <IconButton
                transparent
                size="large"
                onClick={() => onPlayButtonClick(item)}
              >
                <PlayArrow size={50} />
              </IconButton>
              {!!duration && (
                <ProgressBar
                  total={duration}
                  value={time || 0}
                  rounded={false}
                  classes="tw-absolute tw-bottom-0"
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className="tw-px-8 tw-py-4 tw-flex tw-flex-col tw-grow">
        <Link href={getRoute(item, t)} passHref={true}>
          <Typography variant="h4" element="a" classes="tw-mb-1">
            {t('seriesPageEpisodeItemTitleLargeScreen', {
              episodeNumber: item.episodeNumber,
              episodeTitle: item.title,
            })}
          </Typography>
        </Link>
        <Typography variant="paragraph" classes="tw-line-clamp-2">
          {item.teaserDescription}
        </Typography>
        <div className="tw-mt-auto tw-whitespace-nowrap">
          {item.mediaDuration && (
            <Typography element="span">
              {getTranslatedMediaDuration(item.mediaDuration, t)}
            </Typography>
          )}
          {typeof item?.fskRating !== 'undefined' &&
            item?.fskRating !== null && (
              <Typography classes="tw-ml-4" element="span">
                FSK {item.fskRating}
              </Typography>
            )}
          {item.products.access.endDate && (
            <Typography classes="tw-ml-4" element="span">
              {t('seriesPageEpisodeEndDate', {
                date: dayjs(item.products.access.endDate).format(
                  'DD.MM.YYYY HH:mm'
                ),
              })}
            </Typography>
          )}
        </div>
      </div>
      <div className="tw-px-8 tw-flex tw-shrink-0 tw-items-center">
        {shouldShowRentButton(rentProduct, item?.products?.access) && (
          <Button
            key={rentProduct.id}
            title={t('productButtonRentEpisode', {
              price: `${Number(rentProduct.price / 100).toLocaleString(lang, {
                style: 'currency',
                currency: 'EUR',
              })}${'\u00B9'}`,
            })}
            onClick={() => onProductButtonClick(item.iri, rentProduct.iri)}
          />
        )}
      </div>
    </div>
  );
};

export default EpisodeItemLarge;
